import "./currency.scss";
import { ComponentRegistry } from '@ts/utils/ComponentRegistry';

export class Currency {
  private static registry = new ComponentRegistry<Currency>('currency');
  private readonly id: string;
  private readonly input: HTMLInputElement;
  private readonly wrapper: HTMLDivElement;
  private readonly handler: (e: Event) => void;
  private readonly allowNegative: boolean;

  private constructor(
    input: HTMLInputElement,
    allowNegative: boolean = false
  ) {
    if (!(input instanceof HTMLInputElement)) {
      throw new Error('Currency: Element must be an HTMLInputElement');
    }

    this.id = Currency.registry.register(input, this);
    this.allowNegative = allowNegative;
    
    const { wrapper, handler } = this.buildCurrencyInput(input);
    this.wrapper = wrapper;
    this.handler = handler;
  }

  private currencyFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });

  private createDollarSign = (): HTMLSpanElement => {
    const dollarSign = document.createElement('span');
    dollarSign.className = 'currency-symbol';
    dollarSign.textContent = '$';
    return dollarSign;
  }

  private buildCurrencyInput = (input: HTMLInputElement): { wrapper: HTMLDivElement; handler: (e: Event) => void } => {
    const currencyWrapper = document.createElement('div');
    currencyWrapper.className = 'currency-wrapper';

    const dollarSign = this.createDollarSign();

    currencyWrapper.appendChild(dollarSign);
    input.parentNode?.insertBefore(currencyWrapper, input);
    currencyWrapper.appendChild(input);

    const handler = this.createInputHandler(input);
    input.addEventListener('input', handler);

    return { wrapper: currencyWrapper, handler };
  };

  private createInputHandler = (input: HTMLInputElement) => (): void => {
    const isNegative = this.allowNegative && input.value.startsWith('-');
    
    let raw = input.value.replace(/[^0-9.]/g, '');
    
    if (!raw) {
      input.value = isNegative ? '-' : '';
      return;
    }

    const parts = raw.split('.');
    const cleaned = parts[0] + (parts.length > 1 ? '.' + parts[1].slice(0, 2) : '');

    const numeric = Number(cleaned);
    if (Number.isNaN(numeric)) return;

    const finalValue = isNegative ? -numeric : numeric;

    const formatted = this.currencyFormatter.format(finalValue);
    input.value = formatted;
  };

  public static create(
    target: string | HTMLInputElement | NodeListOf<HTMLElement> | HTMLElement[],
    allowNegative: boolean = false
  ): Currency[] | null {
    const inputs = ComponentRegistry.processTargets<Currency, HTMLElement>(
            Currency.registry,
            target,
            [HTMLInputElement]
        ) as HTMLInputElement[];

    if (!inputs.length) return null;
    return inputs.map(input => new Currency(input, allowNegative));
  }

    public static getCurrency(id: string): Currency | null {
      return Currency.registry.get(id) ?? null;
  }

  public destroy(): void {
    this.input.removeEventListener('input', this.handler);
    
    this.wrapper.parentNode?.insertBefore(this.input, this.wrapper);
    this.wrapper.remove();

    Currency.registry.unregister(this.id);
  }
}